// The editor's copy of the questionnaire and the pure moves on it. The
// server stays the judge (modules/intake/intake/template.ts); this keeps Save honest -
// grammar the server would refuse, what it reads by name, an empty
// translation that must be an absent key, never "".

import type {
  IntakeAnswerType,
  IntakeCategory,
  IntakeChecklistItem,
  IntakeConsent,
  IntakeGroup,
  IntakeLanguage,
  IntakeQuestion,
  IntakeRequirement,
  IntakeSection,
  IntakeSubfolder,
  IntakeTemplate,
  IntakeTemplateReserved,
  IntakeText,
  IntakeTranslationClaim,
} from "@duralex/shared";
import type { IntakeTemplatePut } from "../../api/intake";

export type Draft = Pick<IntakeTemplate, "categories" | "sections" | "checklist" | "consents">;

export const GROUPS: IntakeGroup[] = ["IMM", "PI", "CRIM", "DUI", "FAM", "WILLS", "OTHER"];
export const ANSWER_TYPES: IntakeAnswerType[] = [
  "text",
  "longtext",
  "date",
  "yesno",
  "choice",
  "multichoice",
];
export const REQUIREMENTS: IntakeRequirement[] = ["yes", "no", "if-exists"];
export const REQUIREMENT_NAMES: Record<IntakeRequirement, string> = {
  yes: "Required",
  no: "Optional",
  "if-exists": "If it exists",
};
export const SUBFOLDERS: IntakeSubfolder[] = ["01_Client_Info", "06_Evidence"];
export const SECOND_LANGUAGES: IntakeLanguage[] = ["ru", "uk", "uz", "ka", "tr", "es"];

const ID = /^[a-z][a-z0-9_]*$/;
const CODE = /^[A-Z][A-Za-z]*$/;
const DOC_TYPE = /^[A-Z][A-Za-z_]*$/;
const OPTION_VALUE = /^[A-Za-z0-9_]+$/;

export function fromTemplate(t: IntakeTemplate): Draft {
  return structuredClone({
    categories: t.categories,
    sections: t.sections,
    checklist: t.checklist,
    consents: t.consents,
  });
}

/** JSON with object keys sorted - the server's own notion of "unchanged". */
export function canonical(value: unknown): string {
  return JSON.stringify(value, (_key, v: unknown) =>
    v && typeof v === "object" && !Array.isArray(v)
      ? Object.fromEntries(
          Object.keys(v as Record<string, unknown>)
            .sort()
            .map((k) => [k, (v as Record<string, unknown>)[k]]),
        )
      : v,
  );
}

export function isDirty(loaded: IntakeTemplate, draft: Draft): boolean {
  return canonical(fromTemplate(loaded)) !== canonical(draft);
}

export function toPut(
  draft: Draft,
  expectedVersion: number,
  aiTranslations: IntakeTranslationClaim[] = [],
): IntakeTemplatePut {
  return {
    ...structuredClone(draft),
    expectedVersion,
    ...(aiTranslations.length ? { aiTranslations } : {}),
  };
}

/** One language of a label. Blank removes the key: the server refuses "". */
export function withText(text: IntakeText, lang: IntakeLanguage, value: string): IntakeText {
  const next: IntakeText = { ...text };
  if (lang === "en") return { ...next, en: value };
  if (value === "") delete next[lang];
  else next[lang] = value;
  return next;
}

export function slug(en: string, fallback = "question"): string {
  const s = en
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^[^a-z]+/, "")
    .replace(/_+$/, "")
    .slice(0, 60);
  return s || fallback;
}

/** A React key that travels with the row: its id, or id#index while a
    typed duplicate is still on screen (problems() blocks the save). */
export function rowKey(ids: string[], i: number): string {
  const id = ids[i] ?? "";
  return ids.indexOf(id) === i ? id : `${id}#${i}`;
}

/** A new row's id follows its English label until the admin edits the id
    by hand or saves: "Date of marriage" reads date_of_marriage, not
    new_question_3. */
export function followLabel(id: string, was: string, now: string, fallback: string): string {
  return id === slug(was, fallback) ? slug(now, fallback) : id;
}

export function uniqueId(base: string, taken: Set<string>): string {
  if (!taken.has(base)) return base;
  for (let n = 2; ; n += 1) {
    const candidate = `${base.slice(0, 57)}_${n}`;
    if (!taken.has(candidate)) return candidate;
  }
}

export function move<T>(list: T[], index: number, dir: -1 | 1): T[] {
  const to = index + dir;
  if (to < 0 || to >= list.length) return list;
  const next = list.slice();
  const [item] = next.splice(index, 1);
  next.splice(to, 0, item as T);
  return next;
}

export function without<T>(list: T[], index: number): T[] {
  return list.filter((_, i) => i !== index);
}

export function questionIds(draft: Draft): Set<string> {
  return new Set(draft.sections.flatMap((s) => s.questions.map((q) => q.id)));
}

export function newQuestion(taken: Set<string>): IntakeQuestion {
  return {
    id: uniqueId("new_question", taken),
    label: { en: "New question" },
    type: "text",
    required: "no",
    appliesTo: ["ALL"],
  };
}

export function newSection(taken: Set<string>, questionsTaken: Set<string>): IntakeSection {
  // The server wants at least one question in a section.
  return {
    id: uniqueId("new_section", taken),
    title: { en: "New section" },
    questions: [newQuestion(questionsTaken)],
  };
}

export function newCategory(taken: Set<string>): IntakeCategory {
  // Letters only: the server's code grammar has no room for a digit.
  let code = "NewType";
  for (let n = 2; taken.has(code); n += 1) code = `NewType${String.fromCharCode(64 + n)}`;
  return { code, label: { en: "New case type" }, group: "OTHER" };
}

export function newChecklistItem(taken: Set<string>): IntakeChecklistItem {
  return {
    id: uniqueId("new_document", taken),
    label: { en: "New document" },
    required: "if-exists",
    docType: "Other",
    subfolder: "06_Evidence",
    appliesTo: ["ALL"],
  };
}

export function newConsent(taken: Set<string>): IntakeConsent {
  return { id: uniqueId("new_consent", taken), text: { en: "I agree that..." } };
}

/** A copy without one key: the server refuses an empty or a null field,
    so a field that no longer applies must be absent. */
export function omit<T extends object, K extends keyof T>(obj: T, key: K): Omit<T, K> {
  const copy = { ...obj };
  delete copy[key];
  return copy;
}

/** A choice question needs options; any other must not carry them. */
export function retype(q: IntakeQuestion, type: IntakeAnswerType): IntakeQuestion {
  const rest = omit(q, "options");
  const choice = type === "choice" || type === "multichoice";
  if (!choice) return { ...rest, type };
  return {
    ...rest,
    type,
    options: q.options?.length ? q.options : [{ value: "option_1", label: { en: "Option 1" } }],
  };
}

/** requiredFor narrows only a required question; anything else drops it. */
export function setRequired(q: IntakeQuestion, required: IntakeRequirement): IntakeQuestion {
  return required === "yes" ? { ...q, required } : { ...omit(q, "requiredFor"), required };
}

/** Toggle one code. "ALL" stands alone; an empty list falls back to it. */
export function toggleCode(codes: string[], code: string): string[] {
  if (code === "ALL") return codes.includes("ALL") ? codes : ["ALL"];
  const rest = codes.filter((c) => c !== "ALL");
  const next = rest.includes(code) ? rest.filter((c) => c !== code) : [...rest, code];
  return next.length ? next : ["ALL"];
}

/** How many questions and checklist items name a category code. */
export function references(draft: Draft, code: string): number {
  let n = 0;
  for (const q of draft.sections.flatMap((s) => s.questions)) {
    if (q.appliesTo.includes(code)) n += 1;
    if (q.requiredFor?.includes(code)) n += 1;
  }
  for (const d of draft.checklist) if (d.appliesTo.includes(code)) n += 1;
  return n;
}

export interface Problem {
  where: string;
  message: string;
}

/** One line per distinct message, naming the rows: a code renamed under
    twenty questions is one problem in twenty places, not twenty
    problems, and each names where to look. */
export function summarize(list: Problem[]): string[] {
  const wheres = new Map<string, string[]>();
  for (const p of list) {
    const ids = wheres.get(p.message) ?? [];
    if (!p.message.includes(`"${p.where}"`) && !ids.includes(p.where)) ids.push(p.where);
    wheres.set(p.message, ids);
  }
  return [...wheres].map(([message, ids]) => {
    if (!ids.length) return message;
    const shown = ids.slice(0, 3).join(", ");
    const more = ids.length > 3 ? ` and ${ids.length - 3} more` : "";
    return `${message} - ${shown}${more}`;
  });
}

/** What the server would refuse, said before the round trip. */
export function problems(draft: Draft, reserved: IntakeTemplateReserved): Problem[] {
  const out: Problem[] = [];
  const dup = (what: string, ids: string[]) => {
    const seen = new Set<string>();
    for (const id of ids) {
      if (seen.has(id)) out.push({ where: id, message: `duplicate ${what} "${id}"` });
      seen.add(id);
    }
  };
  const english = (where: string, text: IntakeText, what: string) => {
    if (!text.en.trim()) out.push({ where, message: `${what} needs its English text` });
  };
  const known = new Set<string>(["ALL", ...GROUPS, ...draft.categories.map((c) => c.code)]);
  const codesOk = (where: string, codes: string[] | undefined, what: string) => {
    if (codes && codes.length === 0)
      out.push({ where, message: `${what} must name at least one case type` });
    for (const c of codes ?? []) {
      if (!known.has(c))
        out.push({ where, message: `${what} names a case type nobody defined: "${c}"` });
    }
  };

  for (const c of draft.categories) {
    if (!CODE.test(c.code) || c.code.length > 30) {
      out.push({
        where: c.code,
        message: `"${c.code}": a case type code is letters only and starts upper-case`,
      });
    }
    english(c.code, c.label, "The case type");
  }
  dup(
    "case type",
    draft.categories.map((c) => c.code),
  );
  for (const code of reserved.categories) {
    if (!draft.categories.some((c) => c.code === code)) {
      out.push({
        where: code,
        message: `the matter's case type reads "${code}" - reword its label, do not remove it`,
      });
    }
  }

  const questions = draft.sections.flatMap((s) => s.questions);
  for (const s of draft.sections) {
    if (!ID.test(s.id) || s.id.length > 60)
      out.push({ where: s.id, message: `"${s.id}": ids are lower-case snake_case` });
    english(s.id, s.title, "The section");
    if (s.questions.length === 0)
      out.push({ where: s.id, message: `section "${s.id}" needs at least one question` });
  }
  dup(
    "section",
    draft.sections.map((s) => s.id),
  );
  for (const q of questions) {
    if (!ID.test(q.id) || q.id.length > 60)
      out.push({ where: q.id, message: `"${q.id}": ids are lower-case snake_case` });
    english(q.id, q.label, "The question");
    if (q.help && !q.help.en.trim())
      out.push({ where: q.id, message: "The help text needs its English text, or none at all" });
    codesOk(q.id, q.appliesTo, "The question");
    codesOk(q.id, q.requiredFor, "The requirement");
    const choice = q.type === "choice" || q.type === "multichoice";
    if (choice && !(q.options && q.options.length))
      out.push({ where: q.id, message: "A choice question needs at least one option" });
    for (const o of q.options ?? []) {
      if (!OPTION_VALUE.test(o.value) || o.value.length > 40) {
        out.push({
          where: q.id,
          message: `"${o.value}": an option value is letters, digits and underscores`,
        });
      }
      english(q.id, o.label, "The option");
    }
    dup(
      `option of ${q.id}`,
      (q.options ?? []).map((o) => o.value),
    );
  }
  dup(
    "question",
    questions.map((q) => q.id),
  );
  for (const id of reserved.questions) {
    if (!questions.some((q) => q.id === id)) {
      out.push({
        where: id,
        message: `the case record reads "${id}" by name - reword it, do not remove it`,
      });
    }
  }

  for (const d of draft.checklist) {
    if (!ID.test(d.id) || d.id.length > 60)
      out.push({ where: d.id, message: `"${d.id}": ids are lower-case snake_case` });
    english(d.id, d.label, "The document");
    if (!DOC_TYPE.test(d.docType) || d.docType.length > 40) {
      out.push({
        where: d.id,
        message: `"${d.docType}": a document type is letters and underscores, starting upper-case`,
      });
    }
    codesOk(d.id, d.appliesTo, "The document");
  }
  dup(
    "document",
    draft.checklist.map((d) => d.id),
  );
  for (const id of reserved.checklist) {
    if (!draft.checklist.some((d) => d.id === id)) {
      out.push({ where: id, message: `the firm always asks for "${id}" - keep it` });
    }
  }

  for (const c of draft.consents) {
    if (!ID.test(c.id) || c.id.length > 60)
      out.push({ where: c.id, message: `"${c.id}": ids are lower-case snake_case` });
    english(c.id, c.text, "The consent");
  }
  dup(
    "consent",
    draft.consents.map((c) => c.id),
  );
  for (const id of reserved.consents) {
    if (!draft.consents.some((c) => c.id === id)) {
      out.push({ where: id, message: `submit stores "${id}" by id - reword it, do not remove it` });
    }
  }
  return out;
}
